import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import {
  Loader2, Plus, Tablet, KeyRound, ShieldOff, Copy, RefreshCw, AlertTriangle,
} from "lucide-react";

const STATUS_TONE = {
  paired: "bg-success/15 text-success",
  unpaired: "bg-amber-500/15 text-amber-700",
  revoked: "bg-red-500/15 text-red-700",
};

export default function KiosksPage() {
  const [items, setItems] = useState([]);
  const [worksites, setWorksites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pairing, setPairing] = useState(null); // {kiosk_id, name, worksite_name, code}
  const [confirmRevoke, setConfirmRevoke] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [k, w] = await Promise.all([
        api.get("/tenant/kiosks"),
        api.get("/tenant/worksites"),
      ]);
      setItems(k.data.items);
      setWorksites(w.data.items);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const revoke = async (k) => {
    try {
      await api.post(`/tenant/kiosks/${k.id}/revoke`);
      toast.success(`${k.name} revoked`);
      setConfirmRevoke(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const regenerate = async (k) => {
    try {
      const { data } = await api.post(`/tenant/kiosks/${k.id}/regenerate-code`);
      setPairing({ kiosk_id: k.id, name: k.name, worksite_name: k.worksite_name, code: data.pairing_code });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="kiosks-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Time admin</div>
        <h1 className="mt-1 font-display text-3xl font-semibold text-primary">Kiosks</h1>
        <p className="mt-2 text-muted-foreground">
          Provision tablet kiosks for each worksite. Workers sign in with a PIN to punch — no shared account needed.
        </p>
      </div>

      <div>
        <NewKiosk worksites={worksites} onCreated={(k) => { setPairing(k); load(); }} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All kiosks</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Worksite</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Paired at</TableHead>
                <TableHead>IP</TableHead>
                <TableHead>Last seen</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                    <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                  </TableCell>
                </TableRow>
              )}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                    No kiosks yet — create your first one above.
                  </TableCell>
                </TableRow>
              )}
              {items.map((k) => (
                <TableRow key={k.id} data-testid={`kiosk-row-${k.id}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <Tablet className="h-4 w-4 text-muted-foreground" />
                      {k.name}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{k.worksite_name || "—"}</TableCell>
                  <TableCell>
                    <Badge className={`rounded-full capitalize ${STATUS_TONE[k.status] || "bg-muted"}`}>{k.status}</Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs tabular-nums">
                    {k.paired_at ? new Date(k.paired_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{k.ip_pin || "—"}</TableCell>
                  <TableCell className="text-muted-foreground text-xs tabular-nums">
                    {k.last_seen_at ? new Date(k.last_seen_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {k.status !== "revoked" && k.status !== "paired" && (
                        <Button size="sm" variant="ghost" onClick={() => regenerate(k)} data-testid={`kiosk-regen-${k.id}`}>
                          <RefreshCw className="mr-1 h-3.5 w-3.5" /> New code
                        </Button>
                      )}
                      {k.status !== "revoked" && (
                        <Button size="sm" variant="outline" onClick={() => setConfirmRevoke(k)} data-testid={`kiosk-revoke-${k.id}`}>
                          <ShieldOff className="mr-1 h-3.5 w-3.5" /> Revoke
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <PairingCodeDialog pairing={pairing} onClose={() => setPairing(null)} />
      <RevokeDialog k={confirmRevoke} onClose={() => setConfirmRevoke(null)} onConfirm={revoke} />
    </div>
  );
}

function NewKiosk({ worksites, onCreated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [worksiteId, setWorksiteId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const active = useMemo(() => (worksites || []).filter((w) => w.active), [worksites]);

  useEffect(() => {
    if (open) {
      setName("");
      setWorksiteId(active[0]?.id || "");
      setErr("");
    }
  }, [open, active]);

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/tenant/kiosks", { name, worksite_id: worksiteId });
      toast.success("Kiosk created");
      setOpen(false);
      onCreated({ kiosk_id: data.id, name: data.name, worksite_name: data.worksite_name, code: data.pairing_code });
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-kiosk-btn"><Plus className="h-4 w-4" /> New kiosk</Button>
      </DialogTrigger>
      <DialogContent data-testid="new-kiosk-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Tablet className="h-5 w-5" /> New kiosk</DialogTitle>
          <DialogDescription>Creates a one-time 8-character pairing code (valid for 10 minutes).</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input placeholder="Front gate tablet" value={name} onChange={(e) => setName(e.target.value)} data-testid="new-kiosk-name" />
          </div>
          <div className="space-y-2">
            <Label>Worksite</Label>
            <Select value={worksiteId} onValueChange={setWorksiteId}>
              <SelectTrigger data-testid="new-kiosk-worksite"><SelectValue placeholder="Choose worksite" /></SelectTrigger>
              <SelectContent>
                {active.map((w) => (
                  <SelectItem key={w.id} value={w.id}>{w.name} ({w.code})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {err && <Alert variant="destructive"><AlertDescription>{err}</AlertDescription></Alert>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !name || !worksiteId} data-testid="new-kiosk-save">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PairingCodeDialog({ pairing, onClose }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => { setCopied(false); }, [pairing]);
  if (!pairing) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(pairing.code);
      setCopied(true);
      toast.success("Code copied");
    } catch { toast.error("Copy failed — write it down"); }
  };
  return (
    <Dialog open={!!pairing} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="kiosk-pair-code-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-secondary" /> Pair “{pairing.name}”
          </DialogTitle>
          <DialogDescription>
            Enter this code on the tablet at <b>{pairing.worksite_name}</b>. It's shown here only — you can regenerate later.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-xl border bg-muted/30 p-6 text-center">
          <div className="font-display text-4xl font-semibold tracking-[0.35em] tabular-nums text-primary" data-testid="kiosk-new-code">
            {pairing.code}
          </div>
          <div className="mt-2 text-xs text-muted-foreground">Expires in 10 minutes</div>
        </div>
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>Once you close this dialog, this code cannot be re-displayed. Regenerate a new one from the table.</AlertDescription>
        </Alert>
        <DialogFooter>
          <Button variant="outline" onClick={copy} data-testid="kiosk-copy-code">
            <Copy className="mr-1 h-4 w-4" /> {copied ? "Copied" : "Copy code"}
          </Button>
          <Button onClick={onClose} data-testid="kiosk-pair-dialog-done">I've written it down</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevokeDialog({ k, onClose, onConfirm }) {
  if (!k) return null;
  return (
    <Dialog open={!!k} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="kiosk-revoke-dialog">
        <DialogHeader>
          <DialogTitle>Revoke this kiosk?</DialogTitle>
          <DialogDescription>
            <b>{k.name}</b> ({k.worksite_name}) will stop working immediately. You'll need to re-pair the tablet with a new code.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={() => onConfirm(k)} data-testid="kiosk-revoke-confirm">
            Revoke device
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
